import type { Vec2 } from "./Vec2.js";

export function clamp(x: number, min: number, max: number): number {
  return x < min ? min : x > max ? max : x;
}

export function clamp01(x: number): number {
  return clamp(x, 0, 1);
}

/**
 * Closest point on segment AB to point P, plus the parametric t ∈ [0, 1].
 */
export function closestPointOnSegment(p: Vec2, a: Vec2, b: Vec2): { point: Vec2; t: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { point: { x: a.x, y: a.y }, t: 0 };
  const t = clamp(((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq, 0, 1);
  return { point: { x: a.x + dx * t, y: a.y + dy * t }, t };
}

export type SweepHit = {
  toi: number; // time-of-impact in [0, 1] along the sweep
  point: Vec2; // contact point on the segment
  normal: Vec2; // outward unit normal from segment toward the moving circle
};

// Module-scoped scratch returned by sweptCircleVsSegment when a hit happens.
// Callers must consume the result immediately — the next call overwrites it.
// See .claude/skills/writing-zero-alloc-inner-loop/SKILL.md.
const SWEEP_HIT_SCRATCH: SweepHit = {
  toi: 0,
  point: { x: 0, y: 0 },
  normal: { x: 0, y: 0 },
};

/**
 * Analytically sweep a circle of `radius` from `from` to `to` against segment
 * AB. Returns SWEEP_HIT_SCRATCH (mutated in place) with toi ∈ [0, 1] on hit,
 * or null. Caller must read the hit immediately before any subsequent call.
 *
 * The capsule (segment expanded by `radius`) has three boundary regions: two
 * spherical caps at the endpoints and a tube between them. Each candidate t
 * is found by solving the corresponding quadratic / linear equation, then
 * filtered by the regime constraint (which boundary actually applies at that
 * t). The earliest valid candidate wins. Zero allocations in steady state.
 */
export function sweptCircleVsSegment(
  from: Vec2,
  to: Vec2,
  radius: number,
  a: Vec2,
  b: Vec2,
): SweepHit | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const ex = b.x - a.x;
  const ey = b.y - a.y;
  const fx = from.x - a.x;
  const fy = from.y - a.y;
  const ee = ex * ex + ey * ey;
  const dd = dx * dx + dy * dy;
  const rSq = radius * radius;

  // Already touching / penetrating at t=0?
  const s0Num = fx * ex + fy * ey;
  let cx0: number;
  let cy0: number;
  if (ee === 0) {
    cx0 = a.x;
    cy0 = a.y;
  } else {
    let s0 = s0Num / ee;
    if (s0 < 0) s0 = 0;
    else if (s0 > 1) s0 = 1;
    cx0 = a.x + ex * s0;
    cy0 = a.y + ey * s0;
  }
  const cdx0 = from.x - cx0;
  const cdy0 = from.y - cy0;
  const distSq0 = cdx0 * cdx0 + cdy0 * cdy0;
  if (distSq0 <= rSq) {
    const nMag = distSq0 === 0 ? 1 : Math.sqrt(distSq0);
    SWEEP_HIT_SCRATCH.toi = 0;
    SWEEP_HIT_SCRATCH.point.x = cx0;
    SWEEP_HIT_SCRATCH.point.y = cy0;
    SWEEP_HIT_SCRATCH.normal.x = cdx0 / nMag;
    SWEEP_HIT_SCRATCH.normal.y = cdy0 / nMag;
    return SWEEP_HIT_SCRATCH;
  }
  void s0Num;

  if (dd === 0) return null; // no motion and not already touching

  // Treat a degenerate segment (ee == 0) as point-vs-moving-circle, which
  // reduces to the Regime A quadratic below with no regime gating needed.
  let bestToi = Number.POSITIVE_INFINITY;
  let bestPx = 0;
  let bestPy = 0;
  let bestNx = 0;
  let bestNy = 0;

  // Helper: test a candidate t in [0, 1]. `regime`: 0 = cap a, 1 = interior, 2 = cap b.
  // For regime 0 we require sNum(t) <= 0; for 2 we require sNum >= ee; for 1
  // we require 0 <= sNum <= ee. (For ee==0 only regime 0 applies and the
  // sNum constraint is trivially true.)
  const fe = s0Num; // alias
  const de = dx * ex + dy * ey;

  function tryCand(t: number, regime: 0 | 1 | 2): void {
    if (t < 0 || t > 1) return;
    if (t >= bestToi) return;
    if (ee > 0) {
      const sNum = fe + t * de;
      if (regime === 0 && sNum > 0) return;
      if (regime === 2 && sNum < ee) return;
      if (regime === 1 && (sNum < 0 || sNum > ee)) return;
    }
    const px = from.x + t * dx;
    const py = from.y + t * dy;
    let cx: number;
    let cy: number;
    if (regime === 0 || ee === 0) {
      cx = a.x;
      cy = a.y;
    } else if (regime === 2) {
      cx = b.x;
      cy = b.y;
    } else {
      const s = (fe + t * de) / ee;
      cx = a.x + s * ex;
      cy = a.y + s * ey;
    }
    const nx = px - cx;
    const ny = py - cy;
    const nMag = Math.sqrt(nx * nx + ny * ny);
    const safeMag = nMag === 0 ? 1 : nMag;
    bestToi = t;
    bestPx = cx;
    bestPy = cy;
    bestNx = nx / safeMag;
    bestNy = ny / safeMag;
  }

  // Regime A: |P(t) - a|^2 = r^2.
  // dd*t^2 + 2*(f.d)*t + (f.f - r^2) = 0
  const df = fx * dx + fy * dy;
  const ff = fx * fx + fy * fy;
  {
    const aq = dd;
    const bq = 2 * df;
    const cq = ff - rSq;
    const disc = bq * bq - 4 * aq * cq;
    if (disc >= 0) {
      const sqrtDisc = Math.sqrt(disc);
      const inv2a = 1 / (2 * aq);
      tryCand((-bq - sqrtDisc) * inv2a, 0);
      tryCand((-bq + sqrtDisc) * inv2a, 0);
    }
  }

  if (ee > 0) {
    // Regime B: |P(t) - b|^2 = r^2.
    const gx = fx - ex;
    const gy = fy - ey;
    const dg = dx * gx + dy * gy;
    const gg = gx * gx + gy * gy;
    {
      const aq = dd;
      const bq = 2 * dg;
      const cq = gg - rSq;
      const disc = bq * bq - 4 * aq * cq;
      if (disc >= 0) {
        const sqrtDisc = Math.sqrt(disc);
        const inv2a = 1 / (2 * aq);
        tryCand((-bq - sqrtDisc) * inv2a, 2);
        tryCand((-bq + sqrtDisc) * inv2a, 2);
      }
    }

    // Regime I: perpendicular distance to infinite line through ab equals r.
    // (crossFE + t*crossDE)^2 = r^2 * ee.
    const crossFE = fx * ey - fy * ex;
    const crossDE = dx * ey - dy * ex;
    if (crossDE !== 0) {
      const rEMag = radius * Math.sqrt(ee);
      tryCand((rEMag - crossFE) / crossDE, 1);
      tryCand((-rEMag - crossFE) / crossDE, 1);
    }
  }

  if (bestToi === Number.POSITIVE_INFINITY) return null;
  SWEEP_HIT_SCRATCH.toi = bestToi;
  SWEEP_HIT_SCRATCH.point.x = bestPx;
  SWEEP_HIT_SCRATCH.point.y = bestPy;
  SWEEP_HIT_SCRATCH.normal.x = bestNx;
  SWEEP_HIT_SCRATCH.normal.y = bestNy;
  return SWEEP_HIT_SCRATCH;
}

/**
 * True if `point` lies inside the sector centered at `origin`, facing `dir`,
 * with `radius` and `halfAngleCos = cos(halfAngle)`.
 */
export function pointInSector(
  point: Vec2,
  origin: Vec2,
  dir: Vec2,
  radius: number,
  halfAngleCos: number,
): boolean {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  const dist = Math.hypot(dx, dy);
  if (dist > radius) return false;
  if (dist === 0) return true;
  const dotN = (dx * dir.x + dy * dir.y) / dist;
  return dotN >= halfAngleCos;
}
